"use client";

import { useEffect, useRef, useState } from "react";
import AppImage from "@/components/ui/AppImage";

const values = [
  {
    icon: "⚓",
    title: "MARPOL Compliant",
    desc: "Full compliance with international maritime pollution prevention regulations.",
  },
  {
    icon: "🌊",
    title: "Pioneer Since 2016",
    desc: "First movers in marine waste recycling across Indian & UAE ports.",
  },
  {
    icon: "🤝",
    title: "Problem-First Approach",
    desc: "Our motto: solve your operational challenges before they become delays.",
  },
  {
    icon: "♻️",
    title: "Zero Pollution Pledge",
    desc: "Environmentally responsible disposal — protecting our shared oceans.",
  },
];

export default function AboutSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="about" ref={sectionRef} className="py-28 md:py-36 relative overflow-hidden">
      {/* Background accent */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-ocean-700/5 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-gold-500/5 blur-[100px] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Section label */}
        <div
          className={`flex items-center gap-4 mb-16 ${visible ? "animate-fade-up" : "opacity-0"}`}
          style={{ animationDelay: "0.1s" }}
        >
          <div className="w-8 h-px bg-ocean-500" />
          <span className="text-[11px] uppercase tracking-[0.4em] text-ocean-400 font-bold">About BlueSea</span>
        </div>

        <div className="grid lg:grid-cols-12 gap-16 items-start">
          {/* Left: Image stack */}
          <div
            className={`lg:col-span-6 relative ${visible ? "animate-slide-left" : "opacity-0"}`}
            style={{ animationDelay: "0.2s" }}
          >
            {/* Main image */}
            <div className="relative rounded-3xl overflow-hidden aspect-[4/3] shadow-card">
              <AppImage
                src="https://customsgov.co/ship-big.jpg"
                alt="Large commercial cargo vessel at sea, BlueSea Marine Services fleet"
                fill
                className="object-cover hover:scale-105 transition-transform duration-1000"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-900/60 to-transparent" />
            </div>

            {/* Floating overlay card */}
            <div className="absolute -bottom-8 -right-4 md:right-6 glass-card rounded-2xl p-6 max-w-[220px] shadow-card float-2">
              <div className="text-3xl font-black font-display text-gradient-ocean mb-1">20+</div>
              <div className="text-[12px] font-bold text-white mb-1">Years of Expertise</div>
              <div className="text-[11px] text-[#8BA3BC] leading-relaxed">
                Founder's industry experience driving every operation
              </div>
            </div>

            {/* Compliance badge */}
            <div className="absolute top-6 left-6 compliance-badge rounded-xl px-4 py-2.5">
              <div className="text-[10px] uppercase tracking-widest text-gold-400 font-bold">IMO / MARPOL</div>
              <div className="text-[11px] text-white font-semibold">Fully Compliant</div>
            </div>
          </div>

          {/* Right: Content */}
          <div
            className={`lg:col-span-6 lg:pt-8 space-y-10 ${visible ? "animate-slide-right" : "opacity-0"}`}
            style={{ animationDelay: "0.3s" }}
          >
            <div className="space-y-6">
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold leading-[0.9] tracking-tight">
                <span className="text-white">India's Trusted</span><br />
                <span className="text-gradient-ocean italic">Marine Services</span><br />
                <span className="text-white">Partner.</span>
              </h2>
              <p className="text-[#8BA3BC] text-lg leading-relaxed">
                Founded in Mumbai in 2016, BlueSea Marine Services has grown into a 
                regional authority for marine waste management, supply logistics, and 
                independent survey services — operating across 80+ ports in four countries.
              </p>
              <p className="text-[#8BA3BC] leading-relaxed">
                Our team's two decades of frontline maritime experience means we understand 
                the operational pressures vessel owners and shipping agents face. We don't 
                just provide services — we solve problems before they become delays.
              </p>
            </div>

            {/* Values grid */}
            <div className="grid sm:grid-cols-2 gap-5">
              {values?.map((v, i) => (
                <div
                  key={i}
                  className="glass-card rounded-2xl p-5 hover-lift group transition-all duration-300"
                  style={{ animationDelay: `${0.4 + i * 0.1}s` }}
                >
                  <span className="text-2xl block mb-3">{v?.icon}</span>
                  <h4 className="text-white font-bold text-[14px] mb-1.5">{v?.title}</h4>
                  <p className="text-[#8BA3BC] text-[13px] leading-relaxed">{v?.desc}</p>
                </div>
              ))}
            </div>

            {/* CTA */}
            <a
              href="#contact"
              className="inline-flex items-center gap-3 text-ocean-400 font-bold text-sm uppercase tracking-widest hover:text-white transition-colors group"
            >
              Get in Touch
              <div className="w-8 h-px bg-ocean-400 group-hover:w-16 transition-all duration-500" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}